/**
 * TermsUpdateBanner Component
 * Asks a signed-in user to accept the current Terms of Service once, when they
 * haven't accepted it yet (existing users, sign-ups via Google). Doesn't block the app.
 */

import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui'
import { useAuthContext } from '@/components/auth'
import { useTranslation } from '@/hooks'
import { LANDING_URL } from '@/constants/urls'
import { acceptCurrentTerms } from '@/services/api'

export function TermsUpdateBanner() {
  const { t, language } = useTranslation()
  const { profile, refreshProfile } = useAuthContext()
  const [isSaving, setIsSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const terms = profile?.terms
  if (!terms?.current_version || terms.accepted_version === terms.current_version) return null

  const termsUrl = `${LANDING_URL}${language === 'en' ? '/en' : ''}/terms`

  const handleAccept = async () => {
    setIsSaving(true)
    setFailed(false)
    try {
      await acceptCurrentTerms()
      await refreshProfile()
    } catch {
      setFailed(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div role="region" aria-label={t('legal.termsBanner.label')} className="flex-shrink-0 border-b border-accent/20 bg-accent/5">
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
        <FileText className="w-5 h-5 text-accent flex-shrink-0" aria-hidden="true" />
        <p className="flex-1 min-w-[200px] text-body-sm text-gray-700">
          {t('legal.termsBanner.text')}{' '}
          <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            {t('legal.termsBanner.review')}
          </a>
          {failed && <span className="block text-error mt-1">{t('legal.termsBanner.error')}</span>}
        </p>
        <Button size="sm" onClick={handleAccept} isLoading={isSaving}>
          {t('legal.termsBanner.accept')}
        </Button>
      </div>
    </div>
  )
}
